import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ResumeHomieService } from '../../gen2ai/resume-homie.service';
import { ResumeHomieItem } from '../../interfaces-types/resume-homie.interface';
import { AuthenticationService } from '../../clari-auth/authentication.service';

type ExportFormat = 'pdf' | 'docx';
type BaseMode = 'select' | 'paste' | 'upload';

const DEFAULT_RESUME_COLOR = '#0e415d';

@Component({
  selector: 'app-resume-homie',
  templateUrl: './resume-homie.component.html',
  styleUrls: ['./resume-homie.component.scss']
})
export class ResumeHomieComponent implements OnInit, OnDestroy {
  // forms
  searchForm: FormGroup;
  generateForm: FormGroup;

  /** List used by template */
  resumes: ResumeHomieItem[] = [];

  /** View-model lists */
  pinnedResumes: ResumeHomieItem[] = [];
  otherResumes: ResumeHomieItem[] = [];

  /** Filters */
  labels: string[] = [];
  selectedLabel: string | null = null;
  showArchived = false;

  /** UI state */
  isLoading = false;
  error: string | null = null;

  /** Selected base resume (when mode=select) */
  selectedBase: ResumeHomieItem | null = null;

  /** Upload state */
  uploadFileName: string | null = null;

  // palette vibes
  colorPalette: string[] = [
    DEFAULT_RESUME_COLOR,
    '#fff8e1',
    '#e3f2fd',
    '#fce4ec',
    '#e8f5e9',
    '#ede7f6',
    '#f3e5f5',
    '#ffebee',
    '#e0f7fa'
  ];

  private sub = new Subscription();

  constructor(
    private fb: FormBuilder,
    private api: ResumeHomieService,
    private auth: AuthenticationService
  ) {
    this.searchForm = this.fb.group({
      query: ['']
    });

    this.generateForm = this.fb.group({
      title: ['Generated Resume', [Validators.required, Validators.maxLength(180)]],

      /** Base resume acquisition mode */
      baseMode: ['select' as BaseMode, [Validators.required]],

      /** When baseMode = select */
      baseResumeId: [''],

      /** When baseMode = paste/upload */
      baseResumeText: [''],

      /** Job description is always required to generate */
      jobDescription: ['', [Validators.required, Validators.minLength(30)]],

      labels: ['Generated'],
      color: [DEFAULT_RESUME_COLOR],
      pinned: [false],

      /** Optional: when pasting/uploading, allow saving base resume as a new "uploaded" card */
      saveBaseToBoard: [true]
    });
  }

  ngOnInit(): void {
    // reload on auth changes
    this.sub.add(
      this.auth.currentUser$.subscribe(() => void this.load())
    );

    // search live
    this.sub.add(
      this.searchForm.get('query')!.valueChanges.subscribe(() => {
        this.refreshViewFromItems();
      })
    );

    // base resume select live
    this.sub.add(
      this.generateForm.get('baseResumeId')!.valueChanges.subscribe((id: string) => {
        this.selectedBase = this.resumes.find(r => r.id === id) || null;
      })
    );

    // baseMode changes should reset selection/text appropriately
    this.sub.add(
      this.generateForm.get('baseMode')!.valueChanges.subscribe((mode: BaseMode) => {
        this.error = null;
        this.selectedBase = null;
        this.uploadFileName = null;

        if (mode === 'select') {
          this.generateForm.patchValue(
            { baseResumeText: '' },
            { emitEvent: false }
          );
        } else {
          this.generateForm.patchValue(
            { baseResumeId: '' },
            { emitEvent: false }
          );
        }
      })
    );

    void this.load();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /** Convenience getter for templates */
  get isLoggedIn(): boolean {
    return this.auth.currentLoginState;
  }

  /** Show two columns when possible */
  get baseMode(): BaseMode {
    return (this.generateForm.get('baseMode')!.value || 'select') as BaseMode;
  }

  /** Derived lists */
  get filteredPinned(): ResumeHomieItem[] {
    return this.applyFilters(this.pinnedResumes);
  }

  get filteredOthers(): ResumeHomieItem[] {
    return this.applyFilters(this.otherResumes);
  }

  /** Whether generate is allowed */
  get canGenerate(): boolean {
    if (!this.isLoggedIn || this.isLoading) return false;
    if (this.generateForm.invalid) return false;

    // must have a base resume depending on mode
    if (this.baseMode === 'select') {
      return !!(this.generateForm.value.baseResumeId || '').trim();
    }
    // paste/upload mode
    return !!(this.generateForm.value.baseResumeText || '').trim();
  }

  /** Load resumes */
  async load(): Promise<void> {
    this.error = null;

    if (!this.isLoggedIn) {
      this.resumes = [];
      this.pinnedResumes = [];
      this.otherResumes = [];
      this.labels = [];
      this.selectedLabel = null;
      this.selectedBase = null;
      this.generateForm.patchValue({ baseResumeId: '' }, { emitEvent: false });
      return;
    }

    this.sub.add(
      this.api.listResumes().subscribe({
        next: (items) => {
          this.resumes = items || [];
          this.refreshViewFromItems();

          // keep base selection stable
          const baseId = this.generateForm.get('baseResumeId')!.value;
          this.selectedBase = this.resumes.find(r => r.id === baseId) || null;
        },
        error: (e: any) => {
          this.error = e?.message || 'Failed to load resumes.';
        }
      })
    );
  }

  /** Notes-style filters */
  private applyFilters(list: ResumeHomieItem[]): ResumeHomieItem[] {
    let res = [...list];

    if (!this.showArchived) {
      res = res.filter(r => !r.archived);
    }

    if (this.selectedLabel) {
      res = res.filter(r => (r.labels || []).includes(this.selectedLabel!));
    }

    const q: string = this.searchForm.get('query')!.value || '';
    if (q.trim()) {
      const needle = q.toLowerCase();
      res = res.filter(r =>
        (r.title || '').toLowerCase().includes(needle) ||
        (r.content || '').toLowerCase().includes(needle) ||
        (r.source || '').toLowerCase().includes(needle) ||
        (r.labels || []).some(l => l.toLowerCase().includes(needle))
      );
    }

    return res;
  }

  private refreshViewFromItems(): void {
    this.pinnedResumes = this.resumes.filter(r => r.pinned);
    this.otherResumes = this.resumes.filter(r => !r.pinned);

    const labelSet = new Set<string>();
    this.resumes.forEach(r => (r.labels || []).forEach(l => labelSet.add(l)));
    this.labels = Array.from(labelSet).sort();
  }

  selectLabel(label: string | null): void {
    this.selectedLabel = label;
  }

  toggleArchivedView(): void {
    this.showArchived = !this.showArchived;
  }

  /** Upload a resume file as TEXT (txt/md/rtf/etc). */
  async onUploadResumeFile(ev: Event): Promise<void> {
    this.error = null;

    const input = ev.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    if (!file) return;

    this.uploadFileName = file.name;

    try {
      const text = await file.text();
      const cleaned = (text || '').trim();

      if (!cleaned) {
        this.error = 'That file appears to be empty or not readable as text. If it is PDF/DOCX, paste the text instead.';
        this.generateForm.patchValue({ baseResumeText: '' });
        return;
      }

      this.generateForm.patchValue({ baseResumeText: cleaned });
    } catch {
      this.error = 'Failed to read that file. Please try a .txt/.md file or paste the resume text.';
    } finally {
      // allow selecting same file again
      input.value = '';
    }
  }

  /** Save a pasted/uploaded resume as a new "uploaded" card (no generation). */
  saveBaseAsNewCard(): void {
    this.error = null;

    if (!this.isLoggedIn) {
      this.error = 'Please sign in to save resumes to your board.';
      return;
    }

    if (this.baseMode === 'select') {
      this.error = 'Switch to Paste or Upload to save a new resume card.';
      return;
    }

    const content = (this.generateForm.value.baseResumeText || '').trim();
    if (!content || content.length < 30) {
      this.error = 'Please paste/upload at least ~30 characters of resume text.';
      return;
    }

    const title = (this.generateForm.value.title || 'Uploaded Resume').trim();
    const labels = this.parseLabels(this.generateForm.value.labels || '');

    const pinned = !!this.generateForm.value.pinned;
    const color = (this.generateForm.value.color || DEFAULT_RESUME_COLOR).trim();

    this.isLoading = true;

    this.sub.add(
      this.api.createResume({
        title,
        content,
        source: 'uploaded',
        labels: labels.length ? labels : ['Uploaded'],
        pinned,
        color,
        archived: false
      }).subscribe({
        next: () => {
          this.isLoading = false;
          void this.load();
        },
        error: (e: any) => {
          this.isLoading = false;
          this.error = e?.message || 'Failed to save resume card.';
        }
      })
    );
  }

  /** Generate a NEW resume using:
   * - mode=select: base from board
   * - mode=paste/upload: base from textarea/file
   */
  generateFromSelected(): void {
    this.error = null;

    if (!this.isLoggedIn) {
      this.error = 'Please sign in to generate and save resumes.';
      return;
    }

    if (!this.canGenerate) {
      this.generateForm.markAllAsTouched();
      return;
    }

    const title = (this.generateForm.value.title || 'Generated Resume').trim();
    const jobDescription = (this.generateForm.value.jobDescription || '').trim();
    const labels = this.parseLabels(this.generateForm.value.labels || '');

    const pinned = !!this.generateForm.value.pinned;
    const color = (this.generateForm.value.color || DEFAULT_RESUME_COLOR).trim();

    let baseText = '';
    let baseResumeId: string | undefined = undefined;

    if (this.baseMode === 'select') {
      const baseId = (this.generateForm.value.baseResumeId || '').trim();
      const base = this.resumes.find(r => r.id === baseId) || null;

      if (!base) {
        this.error = 'Please choose a resume from your board.';
        return;
      }

      baseText = (base.content || '').trim();
      baseResumeId = base.id;

      if (!baseText) {
        this.error = 'That resume has no text content to use as a base.';
        return;
      }
    } else {
      baseText = (this.generateForm.value.baseResumeText || '').trim();
      if (!baseText) {
        this.error = 'Please paste/upload your current resume text.';
        return;
      }
    }

    const saveBaseToBoard = !!this.generateForm.value.saveBaseToBoard;

    this.isLoading = true;

    // If paste/upload: optionally save the base resume first as uploaded
    if (this.baseMode !== 'select' && saveBaseToBoard) {
      this.sub.add(
        this.api.createResume({
          title: `${title} (Base)`,
          content: baseText,
          source: 'uploaded',
          labels: ['Base', ...(labels.length ? labels : [])],
          pinned: false,
          color: DEFAULT_RESUME_COLOR,
          archived: false
        }).subscribe({
          next: () => {
            // proceed to generate after saving base
            this.doGenerateAndSave(title, baseText, jobDescription, labels, pinned, color, baseResumeId);
          },
          error: (e: any) => {
            this.isLoading = false;
            this.error = e?.message || 'Failed to save base resume to board.';
          }
        })
      );
      return;
    }

    // Otherwise generate immediately
    this.doGenerateAndSave(title, baseText, jobDescription, labels, pinned, color, baseResumeId);
  }

  private doGenerateAndSave(
    title: string,
    baseText: string,
    jobDescription: string,
    labels: string[],
    pinned: boolean,
    color: string,
    baseResumeId?: string
  ): void {
    const payload = {
      title,
      current_resume: baseText,
      job_description: jobDescription,
      labels: labels.length ? labels : ['Generated'],
      pinned,
      color,
      baseResumeId: baseResumeId || undefined,
      teamId: null as any
    };

    this.sub.add(
      this.api.generateAndSave(payload as any).subscribe({
        next: () => {
          this.isLoading = false;

          // keep base selection; clear job description and reset title/labels
          this.generateForm.patchValue({
            title: 'Generated Resume',
            jobDescription: '',
            labels: 'Generated',
            pinned: false,
            color: DEFAULT_RESUME_COLOR
          });

          // if paste/upload, keep base text for iterative use
          if (this.baseMode === 'select') {
            // keep selection
          } else {
            // keep baseResumeText as-is
          }

          void this.load();
        },
        error: (e: any) => {
          this.isLoading = false;
          this.error = e?.error?.details || e?.message || 'Generation failed.';
        }
      })
    );
  }

  private parseLabels(raw: string): string[] {
    const labelsRaw = (raw || '').trim();
    return labelsRaw
      ? labelsRaw.split(',').map((s: string) => s.trim()).filter(Boolean)
      : [];
  }

  /** Card actions */
  async togglePinned(item: ResumeHomieItem, pinned: boolean): Promise<void> {
    if (!this.isLoggedIn) return;

    this.sub.add(
      this.api.updateResume(item.id, { pinned }).subscribe({
        next: () => void this.load(),
        error: (e: any) => (this.error = e?.message || 'Failed to update pin.')
      })
    );
  }

  async archive(item: ResumeHomieItem): Promise<void> {
    if (!this.isLoggedIn) return;

    this.sub.add(
      this.api.updateResume(item.id, { archived: !item.archived }).subscribe({
        next: () => void this.load(),
        error: (e: any) => (this.error = e?.message || 'Failed to archive.')
      })
    );
  }

  async delete(item: ResumeHomieItem): Promise<void> {
    if (!this.isLoggedIn) return;
    if (!confirm('Delete this resume permanently?')) return;

    this.sub.add(
      this.api.deleteResume(item.id).subscribe({
        next: () => void this.load(),
        error: (e: any) => (this.error = e?.message || 'Failed to delete.')
      })
    );
  }

  async changeColor(item: ResumeHomieItem, color: string): Promise<void> {
    if (!this.isLoggedIn) return;

    this.sub.add(
      this.api.updateResume(item.id, { color }).subscribe({
        next: () => void this.load(),
        error: (e: any) => (this.error = e?.message || 'Failed to update color.')
      })
    );
  }

  export(item: ResumeHomieItem, format: ExportFormat): void {
    if (!this.isLoggedIn) {
      this.error = 'Please sign in to export resumes.';
      return;
    }

    this.sub.add(
      this.api.exportResume(item.id, format).subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${(item.title || 'resume').replace(/[^\w\- ]+/g, '')}.${format}`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: (e: any) => (this.error = e?.message || 'Export failed.')
      })
    );
  }

  isLong(item: ResumeHomieItem): boolean {
    return ((item.content || '').length > 260);
  }

  dropPinned(ev: CdkDragDrop<ResumeHomieItem[]>): void {
    if (ev.previousContainer === ev.container) {
      moveItemInArray(this.pinnedResumes, ev.previousIndex, ev.currentIndex);
    } else {
      transferArrayItem(
        ev.previousContainer.data,
        ev.container.data,
        ev.previousIndex,
        ev.currentIndex
      );
    }
  }

  dropOthers(ev: CdkDragDrop<ResumeHomieItem[]>): void {
    if (ev.previousContainer === ev.container) {
      moveItemInArray(this.otherResumes, ev.previousIndex, ev.currentIndex);
    } else {
      transferArrayItem(
        ev.previousContainer.data,
        ev.container.data,
        ev.previousIndex,
        ev.currentIndex
      );
    }
  }
}
